<template>
  <div class="spec-page">
    <div class="page-header">
      <h2 class="page-title">商品规格管理</h2>
      <p class="page-desc">
        先维护「规格维度」（如 颜色、尺码、口味），再为每个维度维护「规格值」（如 红色、XL、香辣）。
        之后在「SKU 管理」中按物品配置维度、为 SKU 选择规格值，系统自动拼接生成 SKU 名称。
      </p>
    </div>

    <div class="spec-layout">
      <!-- 左：规格维度 -->
      <div class="panel">
        <div class="panel-header">
          <span class="panel-title">规格维度</span>
          <button class="add-btn" @click="openSpecModal()">+ 新增维度</button>
        </div>
        <div class="search-bar">
          <input v-model.trim="specKeyword" class="search-input" placeholder="按名称搜索" @keyup.enter="loadSpecs" />
          <button class="search-btn" @click="loadSpecs">搜索</button>
        </div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>名称</th>
                <th style="width:70px">排序</th>
                <th style="width:80px">状态</th>
                <th style="width:140px">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="specs.length === 0">
                <td colspan="4" class="empty-row">暂无规格维度</td>
              </tr>
              <tr
                v-for="spec in specs"
                :key="spec.id"
                :class="{ active: selectedSpec && selectedSpec.id === spec.id }"
                @click="selectSpec(spec)"
              >
                <td>{{ spec.name }}</td>
                <td>{{ spec.sort }}</td>
                <td>
                  <span :class="'status-tag status-' + (spec.status || 0)">
                    {{ spec.status === 1 ? '启用' : '禁用' }}
                  </span>
                </td>
                <td>
                  <button class="action-btn edit" @click.stop="openSpecModal(spec)">编辑</button>
                  <button class="action-btn delete" @click.stop="deleteSpec(spec)">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 右：规格值 -->
      <div class="panel">
        <div class="panel-header">
          <span class="panel-title">
            规格值<span v-if="selectedSpec"> - {{ selectedSpec.name }}</span>
          </span>
          <button class="add-btn" :disabled="!selectedSpec" @click="openSpecValueModal()">
            + 新增规格值
          </button>
        </div>
        <div v-if="!selectedSpec" class="panel-empty">请先在左侧选择一个规格维度</div>
        <div v-else class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>值名称</th>
                <th style="width:70px">排序</th>
                <th style="width:80px">状态</th>
                <th style="width:140px">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="specValues.length === 0">
                <td colspan="4" class="empty-row">该维度下暂无规格值</td>
              </tr>
              <tr v-for="val in specValues" :key="val.id">
                <td>{{ val.name }}</td>
                <td>{{ val.sort }}</td>
                <td>
                  <span :class="'status-tag status-' + (val.status || 0)">
                    {{ val.status === 1 ? '启用' : '禁用' }}
                  </span>
                </td>
                <td>
                  <button class="action-btn edit" @click="openSpecValueModal(val)">编辑</button>
                  <button class="action-btn delete" @click="deleteSpecValue(val)">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 规格维度 弹窗 -->
    <div v-if="specModal.show" class="modal-overlay" @click.self="closeSpecModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ specModal.editing ? '编辑规格维度' : '新增规格维度' }}</h3>
          <button class="close-btn" @click="closeSpecModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>名称 <span class="required">*</span></label>
            <input v-model.trim="specModal.form.name" class="form-control" placeholder="如：颜色、尺码、口味" />
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>排序</label>
              <input v-model.number="specModal.form.sort" type="number" min="0" class="form-control" />
            </div>
            <div class="form-group form-group-half">
              <label>状态</label>
              <select v-model.number="specModal.form.status" class="form-control">
                <option :value="1">启用</option>
                <option :value="0">禁用</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeSpecModal">取消</button>
          <button class="btn-save" @click="saveSpec">保存</button>
        </div>
      </div>
    </div>

    <!-- 规格值 弹窗 -->
    <div v-if="specValueModal.show" class="modal-overlay" @click.self="closeSpecValueModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ specValueModal.editing ? '编辑规格值' : '新增规格值' }}</h3>
          <button class="close-btn" @click="closeSpecValueModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>所属维度</label>
            <input class="form-control" :value="selectedSpec ? selectedSpec.name : ''" disabled />
          </div>
          <div class="form-group">
            <label>值名称 <span class="required">*</span></label>
            <input v-model.trim="specValueModal.form.name" class="form-control" placeholder="如：红色、XL、香辣" />
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>排序</label>
              <input v-model.number="specValueModal.form.sort" type="number" min="0" class="form-control" />
            </div>
            <div class="form-group form-group-half">
              <label>状态</label>
              <select v-model.number="specValueModal.form.status" class="form-control">
                <option :value="1">启用</option>
                <option :value="0">禁用</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeSpecValueModal">取消</button>
          <button class="btn-save" @click="saveSpecValue">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getSpecs,
  createSpec,
  updateSpec,
  deleteSpec,
  getSpecValues,
  createSpecValue,
  updateSpecValue,
  deleteSpecValue
} from '@/api/specs'

export default {
  name: 'ProductSpecs',
  data() {
    return {
      specs: [],
      specKeyword: '',
      selectedSpec: null,
      specValues: [],
      specModal: {
        show: false,
        editing: false,
        form: { id: null, name: '', sort: 0, status: 1 }
      },
      specValueModal: {
        show: false,
        editing: false,
        form: { id: null, spec_id: null, name: '', sort: 0, status: 1 }
      }
    }
  },
  mounted() {
    this.loadSpecs()
  },
  methods: {
    async loadSpecs() {
      try {
        const res = await getSpecs({ name: this.specKeyword || undefined, pageSize: 100 })
        this.specs = (res && res.data && res.data.list) || []
        // 保持选中项与最新数据一致
        if (this.selectedSpec) {
          const found = this.specs.find(s => s.id === this.selectedSpec.id)
          this.selectedSpec = found || null
          if (found) this.loadSpecValues()
        }
      } catch (e) {
        this.specs = []
      }
    },
    selectSpec(spec) {
      this.selectedSpec = spec
      this.loadSpecValues()
    },
    async loadSpecValues() {
      if (!this.selectedSpec) {
        this.specValues = []
        return
      }
      try {
        const res = await getSpecValues({ spec_id: this.selectedSpec.id, pageSize: 100 })
        this.specValues = (res && res.data && res.data.list) || []
      } catch (e) {
        this.specValues = []
      }
    },
    // 规格维度
    openSpecModal(spec) {
      if (spec) {
        this.specModal.editing = true
        this.specModal.form = { id: spec.id, name: spec.name, sort: spec.sort || 0, status: spec.status === 0 ? 0 : 1 }
      } else {
        this.specModal.editing = false
        this.specModal.form = { id: null, name: '', sort: 0, status: 1 }
      }
      this.specModal.show = true
    },
    closeSpecModal() {
      this.specModal.show = false
    },
    async saveSpec() {
      if (!this.specModal.form.name) {
        alert('请输入规格维度名称')
        return
      }
      const form = {
        name: this.specModal.form.name,
        sort: Number(this.specModal.form.sort) || 0,
        status: this.specModal.form.status
      }
      try {
        const res = this.specModal.editing
          ? await updateSpec(this.specModal.form.id, form)
          : await createSpec(form)
        if (res && res.code !== 0) {
          alert(res.message || '保存失败')
          return
        }
        alert(this.specModal.editing ? '更新成功' : '创建成功')
        this.closeSpecModal()
        this.loadSpecs()
      } catch (e) {
        alert('保存失败，请稍后重试')
      }
    },
    async deleteSpec(spec) {
      if (!confirm(`确定删除规格维度「${spec.name}」吗？其下的规格值也会一并删除。`)) return
      try {
        const res = await deleteSpec(spec.id)
        if (res && res.code !== 0) {
          alert(res.message || '删除失败')
          return
        }
        if (this.selectedSpec && this.selectedSpec.id === spec.id) {
          this.selectedSpec = null
          this.specValues = []
        }
        alert('删除成功')
        this.loadSpecs()
      } catch (e) {
        alert('删除失败，请稍后重试')
      }
    },
    // 规格值
    openSpecValueModal(val) {
      if (val) {
        this.specValueModal.editing = true
        this.specValueModal.form = {
          id: val.id,
          spec_id: this.selectedSpec.id,
          name: val.name,
          sort: val.sort || 0,
          status: val.status === 0 ? 0 : 1
        }
      } else {
        this.specValueModal.editing = false
        this.specValueModal.form = { id: null, spec_id: this.selectedSpec.id, name: '', sort: 0, status: 1 }
      }
      this.specValueModal.show = true
    },
    closeSpecValueModal() {
      this.specValueModal.show = false
    },
    async saveSpecValue() {
      if (!this.specValueModal.form.name) {
        alert('请输入规格值名称')
        return
      }
      const form = {
        spec_id: this.selectedSpec.id,
        name: this.specValueModal.form.name,
        sort: Number(this.specValueModal.form.sort) || 0,
        status: this.specValueModal.form.status
      }
      try {
        const res = this.specValueModal.editing
          ? await updateSpecValue(this.specValueModal.form.id, form)
          : await createSpecValue(form)
        if (res && res.code !== 0) {
          alert(res.message || '保存失败')
          return
        }
        alert(this.specValueModal.editing ? '更新成功' : '创建成功')
        this.closeSpecValueModal()
        this.loadSpecValues()
      } catch (e) {
        alert('保存失败，请稍后重试')
      }
    },
    async deleteSpecValue(val) {
      if (!confirm(`确定删除规格值「${val.name}」吗？`)) return
      try {
        const res = await deleteSpecValue(val.id)
        if (res && res.code !== 0) {
          alert(res.message || '删除失败')
          return
        }
        alert('删除成功')
        this.loadSpecValues()
      } catch (e) {
        alert('删除失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.spec-page {
  padding: 4px;
}
.page-header {
  margin-bottom: 16px;
}
.page-title {
  margin: 0 0 6px;
  font-size: 18px;
}
.page-desc {
  margin: 0;
  font-size: 13px;
  color: #888;
  line-height: 1.6;
}
.spec-layout {
  display: flex;
  gap: 16px;
}
.panel {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  min-width: 0;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.panel-title {
  font-size: 15px;
  font-weight: 600;
}
.add-btn {
  padding: 7px 14px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.add-btn:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}
.search-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.search-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
}
.search-btn {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.table-wrap {
  max-height: 60vh;
  overflow: auto;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table th,
.data-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}
.data-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  position: sticky;
  top: 0;
}
.data-table tbody tr {
  cursor: pointer;
}
.data-table tbody tr.active {
  background: #eef4ff;
}
.data-table tbody tr:hover {
  background: #f5f7fa;
}
.empty-row,
.panel-empty {
  text-align: center;
  color: #999;
  padding: 30px 12px;
  font-size: 13px;
}
.panel-empty {
  padding: 40px 12px;
}
.status-tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}
.status-1 {
  background: #e8f5e9;
  color: #2ecc71;
}
.status-0 {
  background: #ffebee;
  color: #e74c3c;
}
.action-btn {
  padding: 4px 9px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 4px;
}
.action-btn.edit {
  background: #3498db;
  color: #fff;
}
.action-btn.delete {
  background: #95a5a6;
  color: #fff;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 460px;
  max-width: 90%;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}
.modal-header h3 {
  margin: 0;
  font-size: 16px;
}
.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}
.modal-body {
  padding: 20px;
}
.form-group {
  margin-bottom: 15px;
}
.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}
.required {
  color: #e74c3c;
}
.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
.form-row {
  display: flex;
  gap: 15px;
}
.form-group-half {
  flex: 1;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}
.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}
.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>
